/**
 * Required credentials/keys on app assignment metadata (credentials_and_keys).
 *
 * metadata.required_credentials:
 *   - string — title only; satisfied by a credential OR key row (entry_type omitted / "any")
 *   - { title, entry_type?: "credentials" | "key" | "any" }
 *       credentials — must be a credential row
 *       key         — must be a key row
 *       any (omit)  — credential or key with matching title
 *
 * Apps Manager textarea (one per line):
 *   Google Classroom Login     → credential or key
 *   credential: State Login    → must be credentials
 *   key: SIS API Token           → must be key
 *
 * Legacy: required_credential_titles (strings → any)
 */

export function normalizeCredentialTitle(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Row type in credentials_keys table */
export function normalizeCkEntryType(value) {
  const t = String(value ?? '')
    .trim()
    .toLowerCase();
  if (t === 'key' || t === 'keys') return 'key';
  return 'credentials';
}

/** Required-slot type in assignment metadata */
export function normalizeRequiredEntryType(value) {
  const t = String(value ?? '')
    .trim()
    .toLowerCase();
  if (t === 'key' || t === 'keys') return 'key';
  if (t === 'credential' || t === 'credentials') return 'credentials';
  if (t === 'any' || t === 'either' || t === '*' || t === '') return 'any';
  return 'any';
}

export function rowEntryType(row) {
  return normalizeCkEntryType(row?.entry_type);
}

function rowMatchesRequiredType(row, requiredEntryType) {
  if (requiredEntryType === 'any') return true;
  return rowEntryType(row) === requiredEntryType;
}

/**
 * @param {string} line
 * @returns {{ title: string, entryType: 'credentials' | 'key' | 'any' } | null}
 */
export function parseRequiredCredentialLine(line) {
  const trimmed = String(line ?? '').trim();
  if (!trimmed) return null;

  const keyMatch = trimmed.match(/^key\s*:\s*(.+)$/i);
  if (keyMatch) {
    const title = keyMatch[1].trim();
    return title ? { title, entryType: 'key' } : null;
  }

  const credMatch = trimmed.match(/^credential\s*:\s*(.+)$/i);
  if (credMatch) {
    const title = credMatch[1].trim();
    return title ? { title, entryType: 'credentials' } : null;
  }

  return { title: trimmed, entryType: 'any' };
}

/**
 * @param {{ title: string, entryType: 'credentials' | 'key' | 'any' }} item
 */
export function formatRequiredCredentialLine(item) {
  if (!item?.title) return '';
  if (item.entryType === 'key') return `key: ${item.title}`;
  if (item.entryType === 'credentials') return `credential: ${item.title}`;
  return item.title;
}

/**
 * @returns {Array<{ title: string, entryType: 'credentials' | 'key' | 'any' }>}
 */
export function parseRequiredCredentialItems(metadata) {
  if (!metadata || typeof metadata !== 'object') return [];
  const raw = metadata.required_credentials ?? metadata.required_credential_titles;
  if (!Array.isArray(raw)) return [];

  const items = [];
  raw.forEach((item) => {
    if (typeof item === 'string') {
      const parsed = parseRequiredCredentialLine(item);
      if (parsed) items.push(parsed);
      return;
    }
    if (item && typeof item === 'object') {
      const title = String(item.title ?? item.label ?? '').trim();
      if (!title) return;
      const hasType =
        item.entry_type != null ||
        item.entryType != null ||
        item.type != null;
      const entryType = hasType
        ? normalizeRequiredEntryType(item.entry_type ?? item.entryType ?? item.type)
        : 'any';
      items.push({ title, entryType });
    }
  });
  return items;
}

/**
 * @returns {{
 *   requiredFlexibleSlots: Array<{ requiredTitle: string, entryType: 'any', missing: boolean, row: object|null }>,
 *   requiredCredentialSlots: Array<{ requiredTitle: string, entryType: 'credentials', missing: boolean, row: object|null }>,
 *   requiredKeySlots: Array<{ requiredTitle: string, entryType: 'key', missing: boolean, row: object|null }>,
 *   extraCredentialRows: object[],
 *   extraKeyRows: object[],
 *   requiredItems: Array<{ title: string, entryType: 'credentials' | 'key' | 'any' }>
 * }}
 */
export function buildCredentialDisplayModel(credentialsKeys, metadata) {
  const requiredItems = parseRequiredCredentialItems(metadata);
  const rows = Array.isArray(credentialsKeys) ? credentialsKeys : [];
  const usedRowIds = new Set();

  const requiredSlots = requiredItems.map(({ title, entryType }) => {
    const normRequired = normalizeCredentialTitle(title);
    const row = rows.find(
      (r) =>
        r?.id != null &&
        !usedRowIds.has(r.id) &&
        normalizeCredentialTitle(r.title) === normRequired &&
        rowMatchesRequiredType(r, entryType)
    );
    if (row) usedRowIds.add(row.id);
    return {
      requiredTitle: title,
      entryType,
      missing: !row,
      row: row || null
    };
  });

  const requiredFlexibleSlots = requiredSlots.filter((s) => s.entryType === 'any');
  const requiredCredentialSlots = requiredSlots.filter((s) => s.entryType === 'credentials');
  const requiredKeySlots = requiredSlots.filter((s) => s.entryType === 'key');

  const extraCredentialRows = rows.filter(
    (r) => r?.id != null && !usedRowIds.has(r.id) && rowEntryType(r) === 'credentials'
  );
  const extraKeyRows = rows.filter(
    (r) => r?.id != null && !usedRowIds.has(r.id) && rowEntryType(r) === 'key'
  );

  return {
    requiredFlexibleSlots,
    requiredCredentialSlots,
    requiredKeySlots,
    extraCredentialRows,
    extraKeyRows,
    requiredItems
  };
}

export function countMissingRequiredCredentials(credentialsKeys, metadata) {
  const { requiredFlexibleSlots, requiredCredentialSlots, requiredKeySlots } =
    buildCredentialDisplayModel(credentialsKeys, metadata);
  return (
    requiredFlexibleSlots.filter((s) => s.missing).length +
    requiredCredentialSlots.filter((s) => s.missing).length +
    requiredKeySlots.filter((s) => s.missing).length
  );
}
